import { ChangeDetectorRef, Component, OnDestroy, TemplateRef, ViewChild, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { StripeService } from "../../services/stripe.service";
import { AuthService } from "../../services/auth.service";
import posthog from 'posthog-js';
import { environment } from "../../../environments/environment";

type GiveawaysPricingOption = "3-month" | "12-month";

type GiveawaysUpgradePlan = {
    id: GiveawaysPricingOption;
    label: string;
    originalPrice: number;
    currentPrice: number;
    description: string[];
    isPopular?: boolean;
};

type PlanPriceConfig = {
    originalPrice: number;
    finalPrice: number;
    priceId: string;
};

@Component({
    selector: "app-giveaways-upgrade-modal",
    imports: [CommonModule],
    templateUrl: "./giveaways-upgrade-modal.component.html",
    styleUrl: "./giveaways-upgrade-modal.component.scss",
    standalone: true,
})
export class GiveawaysUpgradeModalComponent implements OnDestroy {
    private readonly _cdr = inject(ChangeDetectorRef);
    private readonly _modalService = inject(NgbModal);
    private readonly _stripeService = inject(StripeService);
    private readonly _authService = inject(AuthService);

    @ViewChild("giveawaysUpgradeModal") private giveawaysUpgradeModal!: TemplateRef<unknown>;

    private modalRef: NgbModalRef | null = null;

    selectedPricing: GiveawaysPricingOption = "3-month";
    loading = false;

    readonly features = [
        {
            key: "growth",
            title: "Grow your list with curated giveaways",
            description: "200+ avg new subscribers per giveaway. You keep every one.",
        },
        {
            key: "email",
            title: "Turn new readers into book buyers",
            description: "Email marketing, newsletters, and email automations built for authors.",
        },
        {
            key: "site",
            title: "Your own author site, ready in minutes",
            description: "A beautiful page with all your books, retailer links, and reader magnets.",
        },
        {
            key: "network",
            title: "Join a network of 850+ authors",
            description: "Author swaps, group promos, and recommendations that grow your audience.",
        },
    ] as const;

    pricingPlans: GiveawaysUpgradePlan[] = [];

    constructor() {
        this.buildPricingPlans();
    }

    ngOnDestroy(): void {
        if (this.modalRef) {
            this.modalRef.close();
        }
    }

    open(): void {
        posthog.capture("viewed upgrade modal");
        this.selectedPricing = "3-month";
        this.loading = false;
        this.buildPricingPlans();
        this.modalRef = this._modalService.open(this.giveawaysUpgradeModal, {
            centered: true,
            size: "xl",
            windowClass: "giveaways-upgrade-modal",
            backdrop: true,
        });

        this.modalRef.result.finally(() => {
            this.modalRef = null;
        });
    }

    close(): void {
        if (this.modalRef) {
            this.modalRef.close();
            this.modalRef = null;
        }
    }

    selectPricing(planId: GiveawaysPricingOption): void {
        this.selectedPricing = planId;
    }

    async upgrade(): Promise<void> {
        if (this.loading) {
            return;
        }

        const priceId = this.getPriceId();

        if (!priceId) {
            console.error("Price ID not found for selected plan");
            return;
        }

        this.loading = true;
        this._cdr.markForCheck();

        const userEmail = this._authService.user?.email || "";
        const referralId = this.getReferralId();
        const onboardingId = `giveaways-upgrade-${Date.now()}`;

        posthog.capture("upgrade_modal_clicked_upgrade", {
            pricing: this.selectedPricing,
        });
        await new Promise((resolve) => setTimeout(resolve, 1000));

        this._stripeService.startStripeCheckout({
            onboardingId,
            priceId,
            email: userEmail,
            redirectUrl: window.location.pathname,
            referralId,
        });
    }

    private buildPricingPlans(): void {
        const month3 = this.getMonthPrice(3);
        const year1 = this.getYearPrice();

        this.pricingPlans = [
            {
                id: "3-month",
                label: "3 months",
                originalPrice: month3?.originalPrice || 25,
                currentPrice: month3?.finalPrice || 22,
                description: [
                    "Unlock all subscribers from this giveaway",
                    "+ 3 months of the full platform",
                ],
                isPopular: true,
            },
            {
                id: "12-month",
                label: "12 months",
                originalPrice: year1?.originalPrice || 22,
                currentPrice: year1?.finalPrice || 19,
                description: [
                    "Unlock all subscribers from this giveaway + guarantee spot in 1 more",
                    "giveaway + 12 months of the full platform",
                ],
            },
        ];
    }

    private getPriceId(): string | null {
        if (this.selectedPricing === "3-month") {
            return this.getMonthPrice(3)?.priceId || null;
        }

        if (this.selectedPricing === "12-month") {
            return this.getYearPrice()?.priceId || null;
        }

        return null;
    }

    private getMonthPrice(months: 3): PlanPriceConfig | undefined {
        return environment.stripe.prices.v2.month[months]?.["1000"];
    }

    private getYearPrice(): PlanPriceConfig | undefined {
        return environment.stripe.prices.v2.year[1]?.["1000"];
    }

    private getReferralId(): string | null {
        try {
            const url = new URL(window.location.href);
            return url.searchParams.get("rs");
        } catch {
            return null;
        }
    }
}